import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onSnapshot, query, where } from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import { Alert, Box, Button, Typography } from "@mui/material";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PropertyContext from "../contexts/PropertyContext";

const VISIBLE_ROLES = ["frontDesk", "manager"];

const IncomingCallListener = ({ userInfo }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [incomingCalls, setIncomingCalls] = useState([]);
  // Track which callSids the user has manually dismissed so they don't reappear
  const dismissed = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedProperty || selectedProperty === "all-properties") return;
    if (!VISIBLE_ROLES.includes(userInfo?.userType)) return;

    const q = query(
      propertyCollection(selectedClient, selectedProperty, "incomingCalls"),
      where("status", "==", "ringing")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const calls = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => !dismissed.current.has(c.id));
      setIncomingCalls(calls);
    });

    return () => unsubscribe();
  }, [selectedProperty, userInfo?.userType]);

  const handleDismiss = (callId) => {
    dismissed.current.add(callId);
    setIncomingCalls((prev) => prev.filter((c) => c.id !== callId));
  };

  const handleOpenRecord = (call) => {
    handleDismiss(call.id);
    navigate("/callList", { state: { openCallId: call.matchedCallId } });
  };

  const handleAddToList = (call) => {
    handleDismiss(call.id);
    navigate("/callList", {
      state: { prefillPhone: call.from, prefillPhoneFormatted: call.fromFormatted },
    });
  };

  if (incomingCalls.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: 420,
        minWidth: 300,
      }}
    >
      {incomingCalls.map((call) => (
        <Alert
          key={call.id}
          severity="info"
          icon={<PhoneInTalkIcon />}
          onClose={() => handleDismiss(call.id)}
          sx={{ boxShadow: 6, alignItems: "flex-start" }}
        >
          <Typography variant="body2" fontWeight={600}>
            Incoming call
          </Typography>
          <Typography variant="body2">
            {call.matchedName
              ? `${call.matchedName} — ${call.fromFormatted || call.from}`
              : call.fromFormatted || call.from}
          </Typography>
          <Box sx={{ mt: 0.75, display: "flex", gap: 1 }}>
            {call.matchedCallId ? (
              <Button size="small" variant="outlined" onClick={() => handleOpenRecord(call)}>
                Open Record
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={() => handleAddToList(call)}>
                Add to Call List
              </Button>
            )}
          </Box>
        </Alert>
      ))}
    </Box>
  );
};

export default IncomingCallListener;
